import { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { formatCurrency, formatDate, API_URL } from "../lib/utils";
import axios from "axios";
import { toast } from "sonner";
import { Factory, Plus, Trash2, Play, CheckCircle2, XCircle } from "lucide-react";

const ORDER_STATUS = {
  planned: { label: "Planeada", cls: "bg-slate-500/20 text-slate-500" },
  in_progress: { label: "Em Produção", cls: "bg-blue-500/20 text-blue-500" },
  completed: { label: "Concluída", cls: "bg-emerald-500/20 text-emerald-600" },
  cancelled: { label: "Cancelada", cls: "bg-red-500/20 text-red-500" },
};

export default function ProductionPage() {
  const { token } = useAuth();
  const [boms, setBoms] = useState([]);
  const [orders, setOrders] = useState([]);
  const [products, setProducts] = useState([]);
  const [bomModal, setBomModal] = useState(false);
  const [orderModal, setOrderModal] = useState(false);
  const [bomProduct, setBomProduct] = useState("");
  const [components, setComponents] = useState([{ product_id: "", quantity: "" }]);
  const [orderForm, setOrderForm] = useState({ bom_id: "", quantity: "" });

  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  useEffect(() => { fetchAll(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchAll = async () => {
    try {
      const [b, o, p] = await Promise.all([
        axios.get(`${API_URL}/production/boms`, { headers, withCredentials: true }),
        axios.get(`${API_URL}/production/orders`, { headers, withCredentials: true }),
        axios.get(`${API_URL}/products`, { headers, withCredentials: true }),
      ]);
      setBoms(b.data); setOrders(o.data); setProducts(p.data);
    } catch { toast.error("Erro ao carregar produção"); }
  };

  const createBom = async (e) => {
    e.preventDefault();
    const comps = components.filter((c) => c.product_id && c.quantity).map((c) => ({ product_id: c.product_id, quantity: parseFloat(c.quantity) }));
    if (!bomProduct || comps.length === 0) { toast.error("Escolha o produto e os componentes"); return; }
    try {
      await axios.post(`${API_URL}/production/boms`, { product_id: bomProduct, components: comps }, { headers, withCredentials: true });
      toast.success("Ficha técnica criada");
      setBomModal(false); setBomProduct(""); setComponents([{ product_id: "", quantity: "" }]);
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || "Erro"); }
  };

  const deleteBom = async (b) => {
    if (!window.confirm(`Remover ficha técnica de "${b.product_name}"?`)) return;
    await axios.delete(`${API_URL}/production/boms/${b.bom_id}`, { headers, withCredentials: true });
    fetchAll();
  };

  const createOrder = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/production/orders`, { bom_id: orderForm.bom_id, quantity: parseFloat(orderForm.quantity) }, { headers, withCredentials: true });
      toast.success("Ordem de fabrico criada");
      setOrderModal(false); setOrderForm({ bom_id: "", quantity: "" });
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || "Erro"); }
  };

  const orderAction = async (order, action) => {
    try {
      const res = await axios.put(`${API_URL}/production/orders/${order.production_order_id}/${action}`, {}, { headers, withCredentials: true });
      toast.success(res.data.message);
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || "Erro"); }
  };

  return (
    <div className="space-y-6" data-testid="production-page">
      <div>
        <h1 className="text-3xl font-bold font-['Outfit']">Produção</h1>
        <p className="text-muted-foreground">Fichas técnicas (BOM) e ordens de fabrico com consumo de stock</p>
      </div>

      <Tabs defaultValue="boms">
        <TabsList>
          <TabsTrigger value="boms" data-testid="production-tab-boms">Fichas Técnicas ({boms.length})</TabsTrigger>
          <TabsTrigger value="orders" data-testid="production-tab-orders">Ordens de Fabrico ({orders.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="boms" className="space-y-4 mt-4">
          <div className="flex justify-end">
            <Button onClick={() => setBomModal(true)} data-testid="add-bom-btn"><Plus className="w-4 h-4 mr-2" /> Nova Ficha Técnica</Button>
          </div>
          {boms.length === 0 ? (
            <Card><CardContent className="py-12 text-center text-muted-foreground"><Factory className="w-10 h-10 mx-auto mb-3 opacity-40" />Nenhuma ficha técnica</CardContent></Card>
          ) : (
            <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
              {boms.map((b) => (
                <Card key={b.bom_id} data-testid={`bom-card-${b.bom_id}`}>
                  <CardContent className="p-4 space-y-2">
                    <div className="flex items-start justify-between">
                      <p className="font-semibold">{b.product_name}</p>
                      <Button size="sm" variant="ghost" className="text-destructive" onClick={() => deleteBom(b)}><Trash2 className="w-4 h-4" /></Button>
                    </div>
                    <div className="space-y-1">
                      {(b.components || []).map((c, i) => (
                        <div key={i} className="flex justify-between text-xs text-muted-foreground border-b pb-1">
                          <span>{c.name} × {c.quantity}</span>
                          <span className="font-mono">{formatCurrency(c.cost)}</span>
                        </div>
                      ))}
                    </div>
                    <div className="flex justify-between text-sm pt-1">
                      <span>Custo: <strong className="font-mono">{formatCurrency(b.unit_cost)}</strong></span>
                      <span>Margem: <strong className={`font-mono ${b.margin >= 0 ? "text-emerald-600" : "text-red-500"}`}>{formatCurrency(b.margin)}</strong></span>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="orders" className="space-y-4 mt-4">
          <div className="flex justify-end">
            <Button onClick={() => setOrderModal(true)} disabled={boms.length === 0} data-testid="add-production-order-btn"><Plus className="w-4 h-4 mr-2" /> Nova Ordem</Button>
          </div>
          <div className="space-y-2">
            {orders.map((o) => (
              <Card key={o.production_order_id} data-testid={`production-order-${o.production_order_id}`}>
                <CardContent className="p-3 flex items-center justify-between flex-wrap gap-2">
                  <div>
                    <p className="font-mono font-semibold">{o.number} <span className="font-sans font-normal">— {o.quantity}× {o.product_name}</span></p>
                    <p className="text-xs text-muted-foreground">Custo est.: {formatCurrency(o.est_cost)}{o.real_cost != null ? ` · real: ${formatCurrency(o.real_cost)}` : ""} · {formatDate(o.created_at)}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge className={ORDER_STATUS[o.status]?.cls}>{ORDER_STATUS[o.status]?.label || o.status}</Badge>
                    {o.status === "planned" && (
                      <>
                        <Button size="sm" onClick={() => orderAction(o, "start")} data-testid={`start-production-${o.production_order_id}`}><Play className="w-4 h-4 mr-1" /> Iniciar</Button>
                        <Button size="sm" variant="ghost" className="text-destructive" onClick={() => orderAction(o, "cancel")}><XCircle className="w-4 h-4" /></Button>
                      </>
                    )}
                    {o.status === "in_progress" && (
                      <Button size="sm" onClick={() => orderAction(o, "complete")} data-testid={`complete-production-${o.production_order_id}`}><CheckCircle2 className="w-4 h-4 mr-1" /> Concluir</Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
            {orders.length === 0 && <Card><CardContent className="py-10 text-center text-muted-foreground">Nenhuma ordem de fabrico</CardContent></Card>}
          </div>
        </TabsContent>
      </Tabs>

      <Dialog open={bomModal} onOpenChange={setBomModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Nova Ficha Técnica (BOM)</DialogTitle></DialogHeader>
          <form onSubmit={createBom} className="space-y-3">
            <div className="space-y-1">
              <Label>Produto Final *</Label>
              <Select value={bomProduct} onValueChange={setBomProduct}>
                <SelectTrigger data-testid="bom-product-select"><SelectValue placeholder="Produto a fabricar" /></SelectTrigger>
                <SelectContent>{products.map((p) => <SelectItem key={p.product_id} value={p.product_id}>{p.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <Label>Componentes / Ingredientes</Label>
            <div className="space-y-2 max-h-52 overflow-y-auto">
              {components.map((c, i) => (
                <div key={i} className="flex gap-2">
                  <Select value={c.product_id} onValueChange={(v) => { const arr = [...components]; arr[i].product_id = v; setComponents(arr); }}>
                    <SelectTrigger className="flex-1"><SelectValue placeholder="Componente" /></SelectTrigger>
                    <SelectContent>{products.map((p) => <SelectItem key={p.product_id} value={p.product_id}>{p.name}</SelectItem>)}</SelectContent>
                  </Select>
                  <Input type="number" step="0.001" min="0" className="w-24" placeholder="Qtd" value={c.quantity} onChange={(e) => { const arr = [...components]; arr[i].quantity = e.target.value; setComponents(arr); }} />
                  <Button type="button" size="icon" variant="ghost" onClick={() => setComponents(components.filter((_, j) => j !== i))}><Trash2 className="w-4 h-4" /></Button>
                </div>
              ))}
            </div>
            <Button type="button" size="sm" variant="outline" onClick={() => setComponents([...components, { product_id: "", quantity: "" }])}><Plus className="w-4 h-4 mr-1" /> Componente</Button>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setBomModal(false)}>Cancelar</Button>
              <Button type="submit" data-testid="save-bom-btn">Criar</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={orderModal} onOpenChange={setOrderModal}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Nova Ordem de Fabrico</DialogTitle></DialogHeader>
          <form onSubmit={createOrder} className="space-y-3">
            <div className="space-y-1">
              <Label>Ficha Técnica *</Label>
              <Select value={orderForm.bom_id} onValueChange={(v) => setOrderForm({ ...orderForm, bom_id: v })}>
                <SelectTrigger data-testid="order-bom-select"><SelectValue placeholder="Escolher ficha" /></SelectTrigger>
                <SelectContent>{boms.map((b) => <SelectItem key={b.bom_id} value={b.bom_id}>{b.product_name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1"><Label>Quantidade a Produzir *</Label><Input required type="number" step="0.01" min="0.01" value={orderForm.quantity} onChange={(e) => setOrderForm({ ...orderForm, quantity: e.target.value })} data-testid="production-qty-input" /></div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOrderModal(false)}>Cancelar</Button>
              <Button type="submit" disabled={!orderForm.bom_id} data-testid="save-production-order-btn">Criar</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
